import { dispatchToast, getCSRFToken, processResponseTriggers } from "../shared/htmx-helpers";
import { studioConfirm } from "../shared/studio-confirm";
import { signalPublishStateChanged } from "../shared/publish-state-signal";

interface CMSVideoPickerConfig {
  field: string;
  target: string;
  baseURL: string;
  slug: string;
  contextKey: string;
  // slot distinguishes multiple video placements within the same scope/slug
  // (e.g. hero vs. film). "" is the default/hero slot. Optional so the hero
  // widget's config (no slot key emitted server-side) still typechecks.
  slot?: string;
  // projection is the placement's current classification ("flat" |
  // "equirectangular") — only ever emitted server-side for the film slot.
  // Optional so the hero widget's config (no projection key emitted) still
  // typechecks. Read by the READY-state 360° badge (Task 7) and will be
  // written by the classification toggle (Task 8).
  projection?: string;
  videoId: string;
  status: string;
  posterURL: string;
  webmURL: string;
  mp4URL: string;
  error: string;
  tUploading: string;
  tProcessing: string;
  tFailed: string;
  // errorMessages maps a stored transcode_error i18n KEY → localized copy (resolved
  // server-side via t()); the SSE 'error' field carries the same key so the widget
  // never surfaces the raw key.
  errorMessages: Record<string, string>;
}

export function cmsVideoPicker(config: CMSVideoPickerConfig) {
  return {
    field: config.field,
    target: config.target,
    baseURL: config.baseURL,
    slug: config.slug,
    contextKey: config.contextKey,
    slot: config.slot || "",
    projection: config.projection || "",
    videoId: config.videoId || "",
    status: config.status || "", // '' | pending | processing | ready | failed
    posterURL: config.posterURL || "",
    webmURL: config.webmURL || "",
    mp4URL: config.mp4URL || "",
    error: config.error || "", // already-localized initial error (from t())
    errorMessages: config.errorMessages || {},
    uploading: false,
    t_uploading: config.tUploading,
    t_processing: config.tProcessing,
    t_failed: config.tFailed,

    // Resolve a stored/SSE transcode_error KEY to localized copy (falls back to
    // the generic failure label — never surfaces the raw key).
    localizeError(key: string): string {
      return this.errorMessages[key] || (key ? this.t_failed : "");
    },

    _onUploaded: null as ((e: Event) => void) | null,
    _onUploadFailed: null as ((e: Event) => void) | null,

    init() {
      // Fire-and-forget confirm → row created. Filter to THIS field+target.
      this._onUploaded = (e: Event) => {
        const d = (e as CustomEvent).detail || {};
        if (d.field !== this.field || d.target !== this.target) return;
        this.videoId = d.id || this.videoId;
        this.uploading = false;
        this.status = "processing"; // transcode enqueued at confirm
      };
      window.addEventListener("pool-video-uploaded", this._onUploaded);

      this._onUploadFailed = (e: Event) => {
        const d = (e as CustomEvent).detail || {};
        if (d.field !== this.field || d.target !== this.target) return;
        this.uploading = false;
        this.status = "failed";
        // Prefer the uploader's specific message (e.g. "File too large" from a
        // pre-flight size rejection) over the generic transcode-failed copy.
        this.error = d.error || this.t_failed;
      };
      window.addEventListener("pool-video-upload-failed", this._onUploadFailed);
    },

    destroy() {
      if (this._onUploaded) {
        window.removeEventListener("pool-video-uploaded", this._onUploaded);
        this._onUploaded = null;
      }
      if (this._onUploadFailed) {
        window.removeEventListener("pool-video-upload-failed", this._onUploadFailed);
        this._onUploadFailed = null;
      }
    },

    upload(files: FileList | File[]) {
      const list = Array.from(files || []);
      if (list.length === 0) return;
      const store = (window as any).Alpine?.store("uploader");
      if (!store) return;
      this.uploading = true;
      this.error = "";
      // Fire-and-forget: the uploader notifies us via pool-video-uploaded.
      store.addFiles([list[0]], {
        type: "pool_video",
        baseURL: this.baseURL,
        slug: this.slug,
        field: this.field,
        target: this.target,
        slot: this.slot,
      });
    },

    // SSE live update — filter on THIS placement's context_key so two tabs on
    // different placements never cross-update.
    onTranscodeProgress(e: CustomEvent) {
      const raw = (e.detail && (e.detail.data ?? e.detail)) as unknown;
      let payload: {
        context_key?: string;
        status?: string;
        error?: string;
        mp4_url?: string;
        webm_url?: string;
        poster_url?: string;
      } = {};
      try {
        payload = typeof raw === "string" ? JSON.parse(raw) : (raw as any) || {};
      } catch {
        return;
      }
      if (payload.context_key !== this.contextKey) return;
      if (payload.status === "running") {
        this.status = "processing";
        return;
      }
      if (payload.status === "failed") {
        this.status = "failed";
        this.error = this.localizeError(payload.error || "");
        dispatchToast(this.error || this.t_failed, "error");
        return;
      }
      if (payload.status === "complete") {
        this.status = "ready";
        // Populate derivative URLs from the SSE payload so the <video> renders
        // immediately without a page reload.
        if (payload.mp4_url) this.mp4URL = payload.mp4_url;
        if (payload.webm_url) this.webmURL = payload.webm_url;
        if (payload.poster_url) this.posterURL = payload.poster_url;
        // Re-negotiate sources so the browser picks up the new src attributes.
        (this as any).$refs?.preview?.load?.();
        // Reload the render-preview iframe so it picks up the now-ready video.
        // Transcode completion is an EXTERNAL write (server-side; it bypasses the
        // field-autosave flush that normally reloads the preview), so we emit the
        // same signal tour edits use (tour-api.ts) — the editor's single
        // website-content-draft-saved listener calls onExternalDraftSaved →
        // _refreshPreviewReload. Previously this dispatched a bespoke
        // "cms-preview-refresh" event that NOTHING listened to, so the preview
        // kept the OLD/pending video until a manual page reload.
        if (typeof document !== "undefined") {
          document.body.dispatchEvent(
            new CustomEvent("website-content-draft-saved", { bubbles: true }),
          );
        }
      }
    },

    // saveProjection PATCHes the operator's projection override (Task 8) and
    // re-enqueues the transcode server-side. Modelled EXACTLY on
    // savePanoOverride (pano-override.ts): same PATCH/CSRF/resp.ok-gate/dual
    // dirty-signal (website-updated + cms-external-dirty) contract, so the
    // fetch shape never drifts between the two projection-override surfaces.
    // Optimistic: `projection` (bound via x-model on the select) has already
    // flipped by the time this fires — a failed PATCH reverts it here.
    async saveProjection(newValue: string) {
      const prev = this.projection;
      if (!this.videoId || newValue === prev) return;
      this.projection = newValue;
      try {
        const res = await fetch(
          `${this.baseURL}/api/draft/videos/${encodeURIComponent(this.videoId)}/projection`,
          {
            method: "PATCH",
            credentials: "same-origin",
            headers: {
              "X-CSRF-Token": getCSRFToken(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ projection: newValue }),
          },
        );
        if (!res.ok) throw new Error(`save projection ${res.status}`);
        // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
        signalPublishStateChanged();
        document.body.dispatchEvent(new CustomEvent("website-updated", { bubbles: true }));
        window.dispatchEvent(new CustomEvent("cms-external-dirty"));
      } catch (err) {
        console.error("[video-picker] save projection failed", err);
        this.projection = prev; // revert the optimistic x-model flip
        const root = (this as any).$root as HTMLElement;
        const msg = root?.dataset?.projectionFailed || this.t_failed;
        dispatchToast(msg, "error");
      }
    },

    async remove() {
      if (!this.videoId) {
        this.status = "";
        return;
      }
      const root = (this as any).$root as HTMLElement;
      const confirmMsg = root?.dataset?.removeConfirm || "";
      if (confirmMsg && !(await studioConfirm(confirmMsg))) return;
      try {
        const res = await fetch(
          `${this.baseURL}/api/draft/videos/${encodeURIComponent(this.videoId)}`,
          {
            method: "DELETE",
            credentials: "same-origin",
            headers: { "X-CSRF-Token": getCSRFToken() },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        processResponseTriggers(res);
        this.status = "";
        this.videoId = "";
        this.posterURL = "";
        this.mp4URL = "";
        this.webmURL = "";
      } catch (err) {
        console.error("[video-picker] remove failed", err);
        const msg = (this as any).$root?.dataset?.removeFailed || this.t_failed;
        dispatchToast(msg, "error");
      }
    },
  };
}
